import axios from 'axios';

/**
 * CBA Open Banking API Service
 * Handles all interactions with Commonwealth Bank of Australia Open Banking API
 */

class CBAOpenBankingService {
  constructor() {
    this.baseURL = import.meta.env?.VITE_CBA_API_BASE_URL || 'https://api.commbank.com.au/public/cds-au/v1';
    this.clientId = import.meta.env?.VITE_CBA_CLIENT_ID || '';
    this.clientSecret = import.meta.env?.VITE_CBA_CLIENT_SECRET || '';
    this.accessToken = null;
    this.tokenExpiry = null;
  }

  /**
   * Authenticate with CBA Open Banking API
   * @returns {Promise<string>} Access token
   */
  async authenticate() {
    try {
      const response = await axios?.post(`${this.baseURL}/oauth/token`, {
        grant_type: 'client_credentials',
        client_id: this.clientId,
        client_secret: this.clientSecret,
        scope: 'bank:accounts.basic:read bank:transactions:read'
      }, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      this.accessToken = response?.data?.access_token;
      this.tokenExpiry = Date.now() + (response?.data?.expires_in * 1000);
      
      return this.accessToken;
    } catch (error) {
      console.error('CBA Authentication Error:', error?.response?.data || error?.message);
      throw new Error('Failed to authenticate with CBA Open Banking API');
    }
  }

  /**
   * Ensure valid access token
   */
  async ensureAuthenticated() {
    if (!this.accessToken || Date.now() >= this.tokenExpiry) {
      await this.authenticate();
    }
  }

  /**
   * Get all bank accounts
   * @returns {Promise<Array>} List of bank accounts
   */
  async getAccounts() {
    try {
      await this.ensureAuthenticated();

      const response = await axios?.get(`${this.baseURL}/banking/accounts`, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'x-v': '1',
          'x-min-v': '1'
        }
      });

      return response?.data?.data?.accounts || [];
    } catch (error) {
      console.error('Error fetching CBA accounts:', error?.response?.data || error?.message);
      throw new Error('Failed to fetch bank accounts');
    }
  }

  /**
   * Get account balance
   * @param {string} accountId - Account ID
   * @returns {Promise<Object>} Account balance details
   */
  async getAccountBalance(accountId) {
    try {
      await this.ensureAuthenticated();

      const response = await axios?.get(`${this.baseURL}/banking/accounts/${accountId}/balance`, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'x-v': '1',
          'x-min-v': '1'
        }
      });

      return response?.data?.data;
    } catch (error) {
      console.error('Error fetching account balance:', error?.response?.data || error?.message);
      throw new Error('Failed to fetch account balance');
    }
  }

  /**
   * Get transactions for an account
   * @param {string} accountId - Account ID
   * @param {Object} options - Query options (startDate, endDate, minAmount, maxAmount)
   * @returns {Promise<Array>} List of transactions
   */
  async getTransactions(accountId, options = {}) {
    try {
      await this.ensureAuthenticated();

      const params = {
        'oldest-time': options?.startDate || new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)?.toISOString(),
        'newest-time': options?.endDate || new Date()?.toISOString(),
        'page-size': options?.pageSize || 100
      };

      if (options?.minAmount) params['min-amount'] = options?.minAmount;
      if (options?.maxAmount) params['max-amount'] = options?.maxAmount;

      const response = await axios?.get(`${this.baseURL}/banking/accounts/${accountId}/transactions`, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'x-v': '1',
          'x-min-v': '1'
        },
        params
      });

      return response?.data?.data?.transactions || [];
    } catch (error) {
      console.error('Error fetching transactions:', error?.response?.data || error?.message);
      throw new Error('Failed to fetch transactions');
    }
  }

  /**
   * Get transaction details
   * @param {string} accountId - Account ID
   * @param {string} transactionId - Transaction ID
   * @returns {Promise<Object>} Transaction details
   */
  async getTransactionDetail(accountId, transactionId) {
    try {
      await this.ensureAuthenticated();

      const response = await axios?.get(
        `${this.baseURL}/banking/accounts/${accountId}/transactions/${transactionId}`,
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'x-v': '1',
            'x-min-v': '1'
          }
        }
      );

      return response?.data?.data;
    } catch (error) {
      console.error('Error fetching transaction detail:', error?.response?.data || error?.message);
      throw new Error('Failed to fetch transaction details');
    }
  }

  /**
   * Auto-match transactions with cost/invoice entries
   * @param {Array} bankTransactions - Bank transactions from CBA
   * @param {Array} systemRecords - Cost entries or invoices from TradeOps
   * @returns {Array} Matched transactions
   */
  autoMatchTransactions(bankTransactions, systemRecords) {
    const matches = [];
    const unmatchedBank = [];
    const unmatchedSystem = [];

    // Create a copy of system records for matching
    const availableRecords = [...systemRecords];

    bankTransactions?.forEach(bankTx => {
      let bestMatch = null;
      let bestScore = 0;

      availableRecords?.forEach((record, index) => {
        let score = this.calculateMatchScore(bankTx, record);
        
        if (score > bestScore && score >= 0.7) { // 70% confidence threshold
          bestScore = score;
          bestMatch = { record, index };
        }
      });

      if (bestMatch) {
        matches?.push({
          bankTransaction: bankTx,
          systemRecord: bestMatch?.record,
          matchScore: bestScore,
          matchDate: new Date()?.toISOString(),
          matchedBy: 'auto'
        });
        availableRecords?.splice(bestMatch?.index, 1);
      } else {
        unmatchedBank?.push(bankTx);
      }
    });

    return {
      matches,
      unmatchedBankTransactions: unmatchedBank,
      unmatchedSystemRecords: availableRecords
    };
  }

  /**
   * Calculate match score between bank transaction and system record
   * @param {Object} bankTx - Bank transaction
   * @param {Object} record - System record (cost/invoice)
   * @returns {number} Match score (0-1)
   */
  calculateMatchScore(bankTx, record) {
    let score = 0;
    let factors = 0;

    // Amount matching (most important - 40% weight)
    const bankAmount = Math.abs(parseFloat(bankTx?.amount));
    const recordAmount = Math.abs(parseFloat(record?.amount || record?.totalAmount || 0));
    
    if (Math.abs(bankAmount - recordAmount) < 0.01) {
      score += 0.4;
    } else if (Math.abs(bankAmount - recordAmount) < recordAmount * 0.02) { // 2% tolerance
      score += 0.3;
    }
    factors++;

    // Reference number matching (30% weight)
    const bankRef = (bankTx?.reference || bankTx?.description || '')?.toLowerCase();
    const recordRef = (record?.reference || record?.invoiceNumber || record?.id || '')?.toLowerCase();
    
    if (bankRef?.includes(recordRef) || recordRef?.includes(bankRef)) {
      score += 0.3;
    } else if (this.fuzzyMatch(bankRef, recordRef) > 0.7) {
      score += 0.2;
    }
    factors++;

    // Date proximity matching (20% weight)
    const bankDate = new Date(bankTx?.transactionDate || bankTx?.valueDate);
    const recordDate = new Date(record?.date || record?.invoiceDate || record?.created_at);
    const daysDiff = Math.abs((bankDate - recordDate) / (1000 * 60 * 60 * 24));
    
    if (daysDiff <= 3) {
      score += 0.2;
    } else if (daysDiff <= 7) {
      score += 0.1;
    }
    factors++;

    // Transaction type matching (10% weight)
    const isCredit = bankTx?.amount > 0;
    const isReceipt = record?.type === 'receipt' || record?.type === 'invoice';
    const isPayment = record?.type === 'payment' || record?.type === 'cost';
    
    if ((isCredit && isReceipt) || (!isCredit && isPayment)) {
      score += 0.1;
    }
    factors++;

    return score;
  }

  /**
   * Fuzzy string matching for references
   * @param {string} str1 - First string
   * @param {string} str2 - Second string
   * @returns {number} Similarity score (0-1)
   */
  fuzzyMatch(str1, str2) {
    const longer = str1?.length > str2?.length ? str1 : str2;
    const shorter = str1?.length > str2?.length ? str2 : str1;
    
    if (longer?.length === 0) return 1.0;
    
    const editDistance = this.levenshteinDistance(longer, shorter);
    return (longer?.length - editDistance) / longer?.length;
  }

  /**
   * Calculate Levenshtein distance between two strings
   */
  levenshteinDistance(str1, str2) {
    const matrix = [];

    for (let i = 0; i <= str2?.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= str1?.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= str2?.length; i++) {
      for (let j = 1; j <= str1?.length; j++) {
        if (str2?.charAt(i - 1) === str1?.charAt(j - 1)) {
          matrix[i][j] = matrix?.[i - 1]?.[j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix?.[i - 1]?.[j - 1] + 1,
            matrix?.[i]?.[j - 1] + 1,
            matrix?.[i - 1]?.[j] + 1
          );
        }
      }
    }

    return matrix?.[str2?.length]?.[str1?.length];
  }

  /**
   * Transform CBA transaction to TradeOps format
   * @param {Object} cbaTransaction - CBA API transaction
   * @returns {Object} TradeOps formatted transaction
   */
  transformTransaction(cbaTransaction) {
    return {
      id: cbaTransaction?.transactionId,
      date: cbaTransaction?.transactionDate || cbaTransaction?.valueDate,
      description: cbaTransaction?.description,
      reference: cbaTransaction?.reference,
      bankAmount: parseFloat(cbaTransaction?.amount),
      currency: cbaTransaction?.currency || 'AUD',
      type: cbaTransaction?.amount > 0 ? 'receipt' : 'payment',
      status: 'unmatched',
      merchantName: cbaTransaction?.merchantName,
      merchantCategoryCode: cbaTransaction?.merchantCategoryCode,
      apcaNumber: cbaTransaction?.apcaNumber,
      source: 'cba_open_banking',
      syncedAt: new Date()?.toISOString()
    };
  }
}

// Export singleton instance
export default new CBAOpenBankingService();